import React from 'react';
import clsx from 'clsx';
import { ReservationActionTypesEnum } from '@api/ringil-api.v2';
import { getCalendarEventTimeData, stringYMDFromStringInTz } from '@utils/dateWithTz';
import { renderReducedItems } from '@utils/utils.tsx';
import { FlagIcon, sortFlags } from '@components/ringil3/Features/Reservation/flagIcon';
import SecuredArea from '@components/security/SecuredArea';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import SvgTruck from '@icons/truck.svg?react';
import SvgLoadUp from '@icons/LoadUp.svg?react';
import SvgUnload from '@icons/unload.svg?react';
import SvgGoods from '@icons/goods.svg?react';
import SvgBuilding from '@icons/building.svg?react';
import SvgBusiness from '@icons/business.svg?react';
import SvgPerson from '@icons/person.svg?react';
import SvgNote from '@icons/note.svg?react';
import SvgWeight from '@icons/weight.svg?react';
import SvgBox from '@icons/box.svg?react';
import SvgForExpedition from '@icons/forExpedition.svg?react';
import SvgMegaphone from '@icons/megaphone.svg?react';
import { FragmentRenderProps, showIcon } from './types';
import { driverLines, formattedVolume, formattedWeight, orderNotes, supplierNotificationLabel } from './values';

/**
 * Card renderers. Each one owns exactly the markup the hand-written card used for that attribute,
 * including its own data guard and `SecuredArea` wrapper, so a fragment that has nothing to show
 * renders nothing and the zone collapses.
 *
 * Fragments carry no vertical rhythm of their own: the body zone spaces them with `gap-y`. A bottom
 * margin here would be part of the flex item's margin box, so the same fragment placed in the header
 * row would be centred with that margin included and its text would sit visibly above the icons
 * beside it. Spacing belongs to whichever zone the fragment lands in, not to the fragment.
 */

export const CardApplicationId: React.FC<FragmentRenderProps> = ({ data }) => {
  if (!data.reservationApplicationId) return null;
  return <div className="flex items-center text-xs">{data.reservationApplicationId}</div>;
};

export const CardWorkflowIcon: React.FC<FragmentRenderProps> = ({ stateStyle }) => <>{stateStyle?.icon ?? null}</>;

/** Reservation reference and order number. Independent of on-behalf-of, which is its own fragment. */
export const CardName: React.FC<FragmentRenderProps> = ({ data }) => {
  const { reservationName, order } = data;
  if (!reservationName && !order?.orderId) return null;
  return (
    <span className="whitespace-nowrap truncate">
      {reservationName}
      {reservationName && order?.orderId && <>&nbsp;|&nbsp;</>}
      {order?.orderId}
    </span>
  );
};

/**
 * The on-behalf-of company, falling back to the reference and order when the reservation has none —
 * the title as the calendar drew it before layouts were configurable.
 *
 * A fragment of its own rather than a rule inside `CardName`, so a configured layout can still place
 * the company and the reference side by side — that is a legitimate design, just not the default.
 */
export const CardTitle: React.FC<FragmentRenderProps> = props => (props.data.onBehalfOf ? <CardOnBehalfOf {...props} /> : <CardName {...props} />);

export const CardOnBehalfOf: React.FC<FragmentRenderProps> = ({ data }) => {
  const { onBehalfOf } = data;
  if (!onBehalfOf) return null;
  // Trailing margin so it does not run into whatever fragment follows it in the header row, the
  // same way the state icon and the flags carry their own spacing.
  return <span className="whitespace-nowrap truncate mr-1">{onBehalfOf.name ?? onBehalfOf.externalId}</span>;
};

export const CardFlags: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const { flags } = data;
  return (
    <SecuredArea id="calendar-event-card-flags" label="Flags" visible={[{ type: 'data', label: 'reservation has flags', satisfied: (flags?.length ?? 0) > 0 }]}>
      <div className="flex text-xs ml-1 gap-1 items-center text-content-secondary">
        {sortFlags(flags).map(f => (
          <span key={f.reservationFlagType} className="inline-flex items-center" title={tr(`ReservationFlag.${f.reservationFlagType}`, f.reservationFlagType)}>
            <FlagIcon type={f.reservationFlagType} className="w-3 h-3 shrink-0" />
          </span>
        ))}
      </div>
    </SecuredArea>
  );
};

export const CardActionTypes: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const { reservationActionTypes } = data;
  if (!reservationActionTypes || reservationActionTypes.length === 0) return null;
  return (
    <div className="flex gap-x-1 items-center text-xs">
      {reservationActionTypes.indexOf(ReservationActionTypesEnum.LOADING) > -1 && (
        <div title={tr(`ReservationDetail.loading`, `Loading`)}>
          <SvgLoadUp className="w-3 h-3 shrink-0 text-complementary-processing" />
        </div>
      )}
      {reservationActionTypes.indexOf(ReservationActionTypesEnum.UNLOADING) > -1 && (
        <div title={tr(`ReservationDetail.unloading`, `Unloading`)}>
          <SvgUnload className="w-3 h-3 shrink-0" />
        </div>
      )}
    </div>
  );
};

export const CardLabels: React.FC<FragmentRenderProps> = ({ data }) => {
  const { labels } = data;
  return (
    <SecuredArea id="calendar-event-card-labels" label="Labels" visible={[{ type: 'data', label: 'reservation has labels', satisfied: (labels?.length ?? 0) > 0 }]}>
      <div className="flex flex-row h-1 shrink-0 gap-x-0.5 w-full flex-nowrap">
        {labels.map(l => (
          <div className="rounded-[0.25rem] h-full w-full" key={l.labelId} style={{ backgroundColor: l.labelColor }} title={l.labelValue} />
        ))}
      </div>
    </SecuredArea>
  );
};

export const CardCarrierInfo: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const { carrierName, carPlate } = data;
  return (
    <SecuredArea
      id="calendar-event-card-carrier"
      label="Carrier info"
      visible={[{ type: 'data', label: 'reservation has carrier info', satisfied: !!(carrierName || carPlate) }]}
    >
      <CardRow icon={showIcon(config) && <SvgTruck className={rowIconClass} />} multiline>
        {carrierName && `${carrierName} - `}
        {carPlate && `${carPlate}`}
      </CardRow>
    </SecuredArea>
  );
};

export const CardCargoItem: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const { tr } = useTranslationLgs();
  const { orders } = data;
  const orderItems = (orders ?? []).flatMap(order => order.items);
  return (
    <SecuredArea
      id="calendar-event-card-cargo-items"
      label="Cargo items"
      visible={[{ type: 'data', label: 'reservation has order items', satisfied: orderItems.length > 0 }]}
    >
      <CardRow icon={showIcon(config) && <SvgGoods className={rowIconClass} />} multiline>
        {renderReducedItems(orders, tr('Unit.piecesShort', 'pcs'))}
      </CardRow>
    </SecuredArea>
  );
};

export const CardCreated: React.FC<FragmentRenderProps> = ({ data }) => {
  const { createdByFullName, createdAt } = data;
  return (
    <SecuredArea
      id="calendar-event-card-created"
      label="Created by"
      visible={[{ type: 'data', label: 'reservation has created-by info', satisfied: !!createdByFullName }]}
    >
      <div className="flex items-center text-xs">
        <div className="w-4.5 shrink-0">C:</div>
        <div className="min-w-0 truncate">
          {createdByFullName} {createdAt ? stringYMDFromStringInTz(createdAt, 'Europe/Prague') : ''}
        </div>
      </div>
    </SecuredArea>
  );
};

export const CardUpdated: React.FC<FragmentRenderProps> = ({ data }) => {
  const { updatedByFullName, updatedAt } = data;
  return (
    <SecuredArea
      id="calendar-event-card-updated"
      label="Updated by"
      visible={[{ type: 'data', label: 'reservation has updated-by info', satisfied: !!updatedByFullName }]}
    >
      <div className="flex items-center text-xs">
        <div className="w-4.5 shrink-0">U:</div>
        <div className="min-w-0 truncate">
          {updatedByFullName} {updatedAt ? stringYMDFromStringInTz(updatedAt, 'Europe/Prague') : ''}
        </div>
      </div>
    </SecuredArea>
  );
};

/**
 * A plain icon + text row, the shape every card body attribute below uses. Kept local so the
 * spacing matches the hand-written rows above (`text-xs`, 12px icon with a 6px gutter).
 */
const CardRow: React.FC<{ icon?: React.ReactNode; children: React.ReactNode; multiline?: boolean }> = ({ icon, children, multiline }) => (
  <div className="flex items-center text-xs">
    {icon}
    <div className={clsx('min-w-0', multiline ? 'break-words' : 'truncate')}>{children}</div>
  </div>
);

// `h-3` as well as `w-3`: the icon SVGs carry a hardcoded `height="16"`, so sizing the width alone
// leaves them 12x16 (or stretched to the row height by the flex container) instead of square.
const rowIconClass = 'text-content-tertiary w-3 h-3 shrink-0 mr-1.5';

export const CardCustomerName: React.FC<FragmentRenderProps> = ({ data, config }) => {
  if (!data.customerName) return null;
  return <CardRow icon={showIcon(config) && <SvgBusiness className={rowIconClass} />}>{data.customerName}</CardRow>;
};

export const CardLocation: React.FC<FragmentRenderProps> = ({ data, config }) => {
  if (!data.locationName) return null;
  return <CardRow icon={showIcon(config) && <SvgBuilding className={rowIconClass} />}>{data.locationName}</CardRow>;
};

export const CardDriver: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const lines = driverLines(data);
  if (!lines.length) return null;
  return <CardRow icon={showIcon(config) && <SvgPerson className={rowIconClass} />}>{lines.join(' · ')}</CardRow>;
};

export const CardOrderNote: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const notes = orderNotes(data);
  if (!notes.length) return null;
  return (
    <CardRow icon={showIcon(config) && <SvgNote className={rowIconClass} />}>
      <span title={notes.join('\n')}>{notes.join(' · ')}</span>
    </CardRow>
  );
};

export const CardCargoWeight: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const weight = formattedWeight(data);
  if (!weight) return null;
  return <CardRow icon={showIcon(config) && <SvgWeight className={rowIconClass} />}>{weight}</CardRow>;
};

export const CardCargoVolume: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const volume = formattedVolume(data);
  if (!volume) return null;
  return <CardRow icon={showIcon(config) && <SvgBox className={rowIconClass} />}>{volume}</CardRow>;
};

export const CardLinkedExpedition: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const applicationId = data.linkedToExpCoords?.applicationId;
  if (!applicationId) return null;
  // Plain text, not a link: the whole card already navigates to the reservation on click, and a
  // nested anchor inside it would swallow that.
  return <CardRow icon={showIcon(config) && <SvgForExpedition className={rowIconClass} />}>{applicationId}</CardRow>;
};

export const CardLinkedSupplierNotification: React.FC<FragmentRenderProps> = ({ data, config }) => {
  const label = supplierNotificationLabel(data);
  if (!label) return null;
  return <CardRow icon={showIcon(config) && <SvgMegaphone className={rowIconClass} />}>{label}</CardRow>;
};

export const CardSlotDuration: React.FC<FragmentRenderProps> = ({ data }) => {
  const { tr } = useTranslationLgs();
  const timeData = getCalendarEventTimeData(data.timeslot, tr('Unit.minutesShort', 'min'));
  if (!timeData?.range && !timeData?.duration) return null;
  return (
    <div className={clsx('flex items-center text-xs whitespace-nowrap min-w-0')}>
      {timeData?.range && <span>{timeData?.range}</span>}
      <span className="inline-block text-zinc-400 leading-none">&nbsp;|&nbsp;</span>
      {timeData?.duration && <span className="truncate">{timeData?.duration}</span>}
    </div>
  );
};
